import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  createStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { Autocomplete, Rating } from '@material-ui/lab';
import { useGetApms } from './hooks/useGetApms';
import { RatingSubmitData } from './ratingTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
    },
    formLabel: {
      fontSize: '12px',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    ratingInputWrapper: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    ratingInput: {
      marginBottom: '12px',
      marginTop: '8px',
    },
    commentField: {
      width: '100%',
      marginTop: '12px',
      maxWidth: 'none',
      '& textarea': {
        width: '100%',
      },
    },
    apmField: {
      width: '100%',
      marginTop: '12px',
    },
    submitButton: {
      marginTop: '12px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '14px',
      width: '100%',
      height: '40px',
    },
    successMessage: {
      color: theme.palette.success.main,
      fontSize: '14px',
      marginTop: '12px',
      fontWeight: 500,
    },
  }),
);

interface RatingFormProps {
  formTitle?: string;
  onSubmitRating?: (data: RatingSubmitData) => void;
  onSubmitSuccess?: () => void;
}

export const RatingForm: React.FC<RatingFormProps> = ({
  formTitle = 'How would you rate this tool?',
  onSubmitRating,
  onSubmitSuccess,
}) => {
  const classes = useStyles();
  const [allApms] = useGetApms();
  const [userRating, setUserRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [selectedApm, setSelectedApm] = useState<any | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = () => {
    if (userRating === null || selectedApm === null) return;

    onSubmitRating?.({
      rating: userRating,
      comment,
      apmNumber: selectedApm?.ApmNumber,
    });
    setIsSubmitted(true);
    setUserRating(null);
    setComment('');
    setSelectedApm(null);

    setTimeout(() => {
      setIsSubmitted(false);
      onSubmitSuccess?.();
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <Box className={classes.formContainer}>
        <Typography className={classes.successMessage}>
          ✓ Thank you for your rating!
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.formContainer}>
      <Typography
        style={{ fontSize: '18px', fontWeight: 600, textAlign: 'center' }}
      >
        {formTitle}
      </Typography>

      <Box className={classes.ratingInputWrapper}>
        <Rating
          value={userRating}
          onChange={(_event, newValue) => setUserRating(newValue)}
          size="large"
          className={classes.ratingInput}
        />
      </Box>

      <Typography className={classes.formLabel}>
        Tell us more (optional)
      </Typography>

      <TextField
        placeholder="What did you like or dislike?"
        variant="outlined"
        size="small"
        multiline
        minRows={4}
        value={comment}
        onChange={e => setComment(e.target.value)}
        className={classes.commentField}
      />

      <Autocomplete
        options={allApms.sort(
          (a: any, b: any) => -b?.Name.localeCompare(a?.Name),
        )}
        getOptionLabel={(option: any) =>
          option?.Name ? `${option.Name} (${option.ApmNumber})` : ''
        }
        filterOptions={(options: any[], { inputValue }: any) => {
          const lower = inputValue.toLowerCase();
          return options.filter(
            (o: any) =>
              o?.Name?.toLowerCase().includes(lower) ||
              o?.ApmNumber?.toLowerCase().includes(lower),
          );
        }}
        value={selectedApm}
        onChange={(_: any, value: any) => setSelectedApm(value)}
        loading={allApms.length === 0}
        className={classes.apmField}
        renderInput={params => (
          <TextField
            {...params}
            label="APM Number"
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {allApms.length === 0 ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={userRating === null || selectedApm === null}
        className={classes.submitButton}
      >
        Submit
      </Button>
    </Box>
  );
};
